/**
 * GoJobs UI Helper Functions
 * Work type badge colors and salary formatting
 */

/**
 * Get consistent badge color for work type
 * @param {string} workType - Full-time, Part-time, Contract, Casual, etc.
 * @returns {string} - Tailwind classes for badge styling
 */
export const getWorkTypeBadgeStyle = (workType) => {
  const normalizedType = workType?.toLowerCase().replace('-', '_');
  
  const styles = {
    full_time: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 border-blue-200',
    part_time: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border-green-200',
    contract: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 border-purple-200',
    casual: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 border-yellow-200',
    temporary: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 border-orange-200',
    internship: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200 border-pink-200',
    intern: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200 border-pink-200',
  };
  
  return styles[normalizedType] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
};

/**
 * Get human-readable work type label
 * @param {string} workType
 * @returns {string}
 */
export const getWorkTypeLabel = (workType) => {
  if (!workType) return 'Not specified';
  
  // Handle both formats: "Full-time" and "full_time"
  const normalizedType = workType.toLowerCase().replace('-', '_');
  
  const labels = {
    full_time: 'Full-time',
    part_time: 'Part-time',
    contract: 'Contract',
    casual: 'Casual',
    temporary: 'Temporary',
    internship: 'Internship',
    intern: 'Internship',
  };
  
  return labels[normalizedType] || workType;
};

/**
 * Format salary range according to GoFella rules
 * HOURLY: "$45–$60/hr" (NEVER use K for hourly)
 * YEARLY: "$90K–$120K" (Use K for thousands when >= 1000)
 * 
 * @param {number} min - Minimum salary
 * @param {number} max - Maximum salary  
 * @param {string} unit - 'hour' or 'year'
 * @returns {string} - Formatted salary string
 */
export const formatSalary = (min, max, unit = 'year') => {
  if (!min && !max) return 'Salary negotiable';
  
  if (unit === 'hour') {
    // HOURLY: Never use K, always show full number
    if (min && max) {
      return `$${min.toFixed(0)}–$${max.toFixed(0)}/hr`;
    } else if (min) {
      return `From $${min.toFixed(0)}/hr`;
    } else {
      return `Up to $${max.toFixed(0)}/hr`;
    }
  } else {
    // YEARLY: Use K for thousands
    const formatYearly = (value) => {
      if (value >= 1000) {
        return `$${(value / 1000).toFixed(0)}K`;
      }
      return `$${value.toFixed(0)}`;
    };
    
    if (min && max) {
      return `${formatYearly(min)}–${formatYearly(max)}`;
    } else if (min) {
      return `From ${formatYearly(min)}`;
    } else {
      return `Up to ${formatYearly(max)}`;
    }
  }
};

/**
 * Calculate days until expiry
 * @param {string} expiryDate - ISO datetime string
 * @returns {number} - Days remaining (0 if expired)
 */
export const getDaysUntilExpiry = (expiryDate) => {
  if (!expiryDate) return null;
  const days = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
  return days > 0 ? days : 0;
};

/**
 * Get expiry label for job listing
 * @param {string} expiryDate - ISO datetime string
 * @returns {string} - "Closes today", "Closes in 1 day", "Closes in X days"
 */
export const getExpiryLabel = (expiryDate) => {
  const days = getDaysUntilExpiry(expiryDate);
  
  if (days === null) return '';
  if (days === 0) return 'Closes today';
  if (days === 1) return 'Closes in 1 day';
  return `Closes in ${days} days`;
};

/**
 * Check if job is expired
 * @param {string} expiryDate
 * @returns {boolean}
 */
export const isJobExpired = (expiryDate) => {
  if (!expiryDate) return false;
  return new Date(expiryDate) < new Date();
};
